import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Search, ChevronDown } from 'lucide-react';
import moment from 'moment';

import ClubbieTable from '../../components/ClubbieTable/ClubbieTable';
import DatePicker from '../../components/DatePicker/DatePicker';
import EmptyState from '../../components/EmptyState';
import { config } from '../../utils/config';
import ClubbieLoader from '../../components/Loader/ClubbieLoader';
import useTournamentRole from '../../hooks/useTournamentRole';
import CreatePaymentModal from '../../components/CreatePaymentModal/CreatePaymentModal';
import SyncStripeAccount from '../../components/Payments/SyncStripeAccount';

const getCurrencySymbol = (currency) => {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 })
      .format(0)
      .replace(/\d/g, '')
      .trim();
  } catch {
    return currency;
  }
};

const DATE_FILTER_OPTIONS = [
  { label: 'All dates', value: 'all' },
  { label: 'This week', value: 'week' },
  { label: 'This month', value: 'month' },
  { label: 'This year', value: 'year' },
  { label: 'Custom dates', value: 'custom' },
];

const PAYMENT_FILTER_OPTIONS = [
  { label: 'All payments', value: 'all' },
  { label: 'Past', value: 'past' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Due today', value: 'due today' },
];

const PaymentsSearchFilters = ({ searchQuery, onSearchChange, dateFilter, onDateFilterChange, paymentFilter, onPaymentFilterChange }) => {
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customRangeStart, setCustomRangeStart] = useState(null);
  const [customRangeEnd, setCustomRangeEnd] = useState(null);
  const dateRef = useRef(null);
  const paymentRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore clicks inside DatePicker calendar portals
      if (e.target.closest('[style*="z-index: 9999"]') || e.target.closest('[style*="z-index:9999"]')) return;

      if (dateRef.current && !dateRef.current.contains(e.target)) {
        setShowDateDropdown(false);
        setShowCustomRange(false);
      }
      if (paymentRef.current && !paymentRef.current.contains(e.target)) {
        setShowPaymentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (value) => {
    if (value === 'custom') {
      setShowCustomRange(true);
      return;
    }
    let from = '';
    let to = '';
    if (value === 'week') {
      from = moment().startOf('isoWeek').format('YYYY-MM-DD');
      to = moment().endOf('isoWeek').format('YYYY-MM-DD');
    } else if (value === 'month') {
      from = moment().startOf('month').format('YYYY-MM-DD');
      to = moment().endOf('month').format('YYYY-MM-DD');
    } else if (value === 'year') {
      from = moment().startOf('year').format('YYYY-MM-DD');
      to = moment().endOf('year').format('YYYY-MM-DD');
    }
    onDateFilterChange(value, from, to);
    setShowDateDropdown(false);
    setShowCustomRange(false);
  };

  const handleCustomRangeApply = () => {
    if (customRangeStart && customRangeEnd) {
      const label = `${moment(customRangeStart).format('D MMM')} - ${moment(customRangeEnd).format('D MMM')}`;
      onDateFilterChange('custom', moment(customRangeStart).format('YYYY-MM-DD'), moment(customRangeEnd).format('YYYY-MM-DD'), label);
      setShowDateDropdown(false);
      setShowCustomRange(false);
    }
  };

  const dateLabel = DATE_FILTER_OPTIONS.find((o) => o.value === dateFilter.value)?.label || dateFilter.label || 'All dates';
  const paymentLabel = PAYMENT_FILTER_OPTIONS.find((o) => o.value === paymentFilter)?.label || 'All payments';

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
      {/* Search */}
      <div className="relative w-full md:w-96">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          <Search size={18} />
        </div>
        <input
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full bg-app-surface border border-app-surface rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 text-sm text-app-text-muted">
        {/* Date filter */}
        <div className="relative" ref={dateRef}>
          <button
            onClick={() => { setShowDateDropdown(!showDateDropdown); setShowPaymentDropdown(false); }}
            className="flex items-center gap-1 text-[#FEFCEB] font-medium bg-[#082441] px-4 py-2.5 rounded-3xl hover:border-[#00E7FF] transition-colors"
          >
            {dateLabel}
            <ChevronDown size={18} className={`transition-transform duration-200 shrink-0 ${showDateDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showDateDropdown && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
              {DATE_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleDateSelect(option.value)}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 ${
                    dateFilter.value === option.value ? 'text-app-accent' : 'text-[#FEFCEB] hover:bg-[#0D1B29]'
                  }`}
                >
                  {option.label}
                </button>
              ))}

              {showCustomRange && (
                <div className="p-3 border-t border-[#192634] space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs text-app-text-secondary">From</label>
                    <DatePicker value={customRangeStart} onChange={setCustomRangeStart} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-app-text-secondary">To</label>
                    <DatePicker value={customRangeEnd} onChange={setCustomRangeEnd} />
                  </div>
                  <button
                    onClick={handleCustomRangeApply}
                    disabled={!customRangeStart || !customRangeEnd}
                    className="w-full bg-[#00E7FF] text-[#021529] py-2 rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Payment filter */}
        <div className="relative" ref={paymentRef}>
          <button
            onClick={() => { setShowPaymentDropdown(!showPaymentDropdown); setShowDateDropdown(false); }}
            className="flex items-center gap-1 text-[#FEFCEB] font-medium bg-[#082441] px-4 py-2.5 rounded-3xl hover:border-[#00E7FF] transition-colors"
          >
            {paymentLabel}
            <ChevronDown size={18} className={`transition-transform duration-200 shrink-0 ${showPaymentDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showPaymentDropdown && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
              {PAYMENT_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onPaymentFilterChange(option.value);
                    setShowPaymentDropdown(false);
                  }}
                  className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 ${
                    paymentFilter === option.value ? 'text-app-accent' : 'text-[#FEFCEB] hover:bg-[#0D1B29]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PaymentsList = () => {
  const navigate = useNavigate();
  const { permissions } = useTournamentRole();

  // Get tournament context from Redux to filter payments
  const { selectedTournament, selectedCategory } = useSelector((state) => state.tournament);
  const currentUser = useSelector((state) => state.user.user);
  const isStripeOnboarded = !!currentUser?.stripe?.connectedAccount?.isOnboard;
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showSyncStripe, setShowSyncStripe] = useState(false);

  const handleCreatePaymentClick = () => {
    if (isStripeOnboarded) {
      setIsCreateModalOpen(true);
    } else {
      setShowSyncStripe(true);
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({ value: 'all', from: '', to: '', label: 'All dates' });
  const [paymentFilter, setPaymentFilter] = useState('all');
  const hasEverLoaded = useRef(false);

  const [stats, setStats] = useState({
    totalPending: 0,
    totalPaid: 0,
    totalOverdue: 0,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: 'description',
        header: () => <span className="text-[#FEFCEB]">Description</span>,
        cell: ({ row }) => (
          <div
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => {
              if (row.original.payFor === 'Subscription' || row.original.payFor === 'Custom') {
                navigate(`/payments/details/${row.original.id}`);
              } else {
                navigate(`/payments/${row.original.id}`);
              }
            }}
          >
            {row.original.team?.crest && (
              <div
                className="w-8 h-8 rounded-full bg-cover bg-center bg-white"
                style={{ backgroundImage: `url(${row.original.team.crest})` }}
              />
            )}
            <div>
              <span className="text-[#FEFCEB] block">{row.original.description}</span>
              {row.original.minor && (
                <span className="text-[#7F8585] text-xs">For: {row.original.minorName}</span>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'team',
        header: () => <span className="text-[#FEFCEB]">Team</span>,
        cell: ({ row }) => (
          <span className="text-[#FEFCEB]">{row.original.team?.title || '-'}</span>
        ),
      },
      {
        accessorKey: 'date',
        header: () => <span className="text-[#FEFCEB]">Date</span>,
        cell: ({ row }) => (
          <span className="text-[#FEFCEB]">{row.original.date}</span>
        ),
      },
      {
        accessorKey: 'payFor',
        header: () => <span className="text-[#FEFCEB]">Type</span>,
        cell: ({ row }) => (
          <span className="text-[#FEFCEB]">{row.original.payFor}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: () => <span className="text-[#FEFCEB]">Status</span>,
        cell: ({ row }) => {
          const status = row.original.status;
          const statusColors = {
            paid: 'bg-green-500/20 text-green-400 border-green-500/30',
            unpaid: 'bg-red-500/20 text-red-400 border-red-500/30',
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'pending-cash-approval': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
            canceled: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
          };
          return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[status] || statusColors.unpaid}`}>
              {status}
            </span>
          );
        },
      },
      {
        accessorKey: 'amount',
        header: () => <span className="text-[#FEFCEB]">Amount</span>,
        cell: ({ row }) => (
          <span className="text-[#FEFCEB] font-medium">{getCurrencySymbol(row.original.currency)}{row.original.amount.toFixed(2)}</span>
        ),
      },
      {
        accessorKey: 'actions',
        header: () => <span className="text-[#FEFCEB]">Actions</span>,
        cell: ({ row }) => (
          <div className="relative action-menu-container">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActionMenuOpen(actionMenuOpen === row.original.id ? null : row.original.id);
              }}
              className="p-2 text-[#7F8585] hover:text-[#FEFCEB] transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="4" r="1.5" fill="currentColor" />
                <circle cx="10" cy="10" r="1.5" fill="currentColor" />
                <circle cx="10" cy="16" r="1.5" fill="currentColor" />
              </svg>
            </button>
            {actionMenuOpen === row.original.id && (
              <div className="absolute right-0 mt-2 w-48 bg-[#011224] border-2 border-[#192634] rounded-lg shadow-lg z-50">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/payments/details/${row.original.id}`);
                    setActionMenuOpen(null);
                  }}
                  className="w-full text-left px-4 py-3 text-[#FEFCEB] hover:bg-[#082441] transition-colors flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M2 8s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                  View payment
                </button>
                {permissions.canManagePayments && row.original.payFor === 'Custom' && row.original.status !== 'paid' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/payments/details/${row.original.id}`);
                      setActionMenuOpen(null);
                    }}
                    className="w-full text-left px-4 py-3 text-[#FEFCEB] hover:bg-[#082441] transition-colors flex items-center gap-2"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M11.3 1.7a1 1 0 011.4 0l1.6 1.6a1 1 0 010 1.4l-9 9-3 .3.3-3 9-9z" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    Edit payment
                  </button>
                )}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleExportPaymentData(row.original.id);
                    setActionMenuOpen(null);
                  }}
                  className="w-full text-left px-4 py-3 text-[#FEFCEB] hover:bg-[#082441] transition-colors flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M14 10v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2m4-4L8 2m0 0l2 4M8 2v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Export payment data
                </button>
                {permissions.canManagePayments && row.original.status !== 'paid' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePayment(row.original.id);
                      setActionMenuOpen(null);
                    }}
                    className="w-full text-left px-4 py-3 text-red-400 hover:bg-[#082441] transition-colors flex items-center gap-2 border-t border-[#192634]"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Delete payment
                  </button>
                )}
              </div>
            )}
          </div>
        ),
      },
    ],
    [navigate, actionMenuOpen]
  );

  // Refetch payments when filters or tournament context changes
  useEffect(() => {
    fetchPayments();
  }, [dateFilter, paymentFilter, selectedTournament?.id, selectedCategory?._id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuOpen && !event.target.closest('.action-menu-container')) {
        setActionMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]);

  const fetchPayments = async () => {
    // Backend rejects non-organizers with 403 — short-circuit so we never fire the doomed request.
    if (!permissions.canManagePayments) {
      setPayments([]);
      setStats({ totalPending: 0, totalPaid: 0, totalOverdue: 0 });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();

      if (!selectedTournament?.id) {
        setPayments([]);
        setStats({ totalPending: 0, totalPaid: 0, totalOverdue: 0 });
        setLoading(false);
        return;
      }

      if (selectedCategory?._id) {
        queryParams.append('categoryId', selectedCategory._id);
      }

      if (dateFilter.from && dateFilter.to) {
        queryParams.append('dateFilter', encodeURIComponent(JSON.stringify({ startDate: dateFilter.from, endDate: dateFilter.to })));
      }

      if (paymentFilter !== 'all') {
        queryParams.append('typeFilter', paymentFilter);
      }

      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/tournament-payments/${selectedTournament.id}?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        // Handle specific error cases
        if (response.status === 403) {
          // Access denied - clear payments and show empty state
          setPayments([]);
          setStats({ totalPending: 0, totalPaid: 0, totalOverdue: 0 });
          setLoading(false);
          return;
        }
        
        if (response.status === 404) {
          // Tournament not found - clear payments
          setPayments([]);
          setStats({ totalPending: 0, totalPaid: 0, totalOverdue: 0 });
          setLoading(false);
          return;
        }
        
        throw new Error(`Failed to fetch payments: ${response.status}`);
      }

      const data = await response.json();

      // Handle empty results gracefully
      if (!data.results || !Array.isArray(data.results)) {
        setPayments([]);
        setStats({ totalPending: 0, totalPaid: 0, totalOverdue: 0 });
        setLoading(false);
        return;
      }

      const formattedPayments = data.results.map(payment => {
        // Derive aggregated status
        let derivedStatus = 'unpaid';
        if (payment.totalPaid > 0 && payment.totalPending === 0) derivedStatus = 'paid';
        else if (payment.totalOverdue > 0) derivedStatus = 'overdue';
        else if (payment.cashApprovalCount > 0) derivedStatus = 'pending-cash-approval';
        else if (payment.totalPending > 0) derivedStatus = 'unpaid';

        return {
          id: payment.requestId,
          requestId: payment.requestId,
          description: payment.customReference || 'Tournament Payment',
          teamTitle: payment.teamTitle,
          date: payment.billingDate ? new Date(payment.billingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : new Date(payment.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
          payFor: payment.payFor,
          status: derivedStatus,
          amount: payment.singleAmount,
          totalAmount: payment.totalAmount,
          totalPaid: payment.totalPaid,
          totalPending: payment.totalPending,
          totalOverdue: payment.totalOverdue,
          cashApprovalCount: payment.cashApprovalCount,
          currency: payment.currency || 'GBP',
          billingDate: payment.billingDate,
          recurrence: payment.recurrence,
        };
      });

      setPayments(formattedPayments);
      if (formattedPayments.length > 0) hasEverLoaded.current = true;
      if (data.totals) {
        setStats({
          totalPending: data.totals.totalPending || 0,
          totalPaid: data.totals.totalReceived || 0,
          totalOverdue: data.totals.totalOverdue || 0,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const hasPayments = payments.length > 0;
  const hasActiveFilters = dateFilter.value !== 'all' || paymentFilter !== 'all' || searchQuery.trim();
  const showControls = hasPayments || hasEverLoaded.current;

  const filteredPayments = useMemo(() => {
    if (!searchQuery.trim()) return payments;
    const q = searchQuery.toLowerCase();
    return payments.filter((p) =>
      p.description?.toLowerCase().includes(q) ||
      p.team?.title?.toLowerCase().includes(q)
    );
  }, [payments, searchQuery]);

  const handleExportPaymentData = async (paymentId) => {
    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/${paymentId}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to export payment data');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-${paymentId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert('Failed to export payment data');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    try {
      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete payment');

      await fetchPayments();
      alert('Payment deleted successfully');
    } catch (error) {
      alert('Failed to delete payment');
    }
  };

  const isEmpty = (!loading && !hasPayments && !hasActiveFilters && !hasEverLoaded.current) || showSyncStripe;

  if (!permissions.canManagePayments) {
    return (
      <div className="bg-[#000F1F] text-[#FEFCEB] h-full flex flex-col p-[18px] lg:px-8 font-sans">
        <div className="flex-1 min-h-0 flex flex-col">
          <EmptyState
            heading="Payments aren't available for your role"
            description="Only the tournament owner and admins can view payment information for this competition."
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#000F1F] text-[#FEFCEB] p-[18px] lg:px-8 font-sans ${isEmpty ? "h-full flex flex-col" : "min-h-screen"}`}>
      <div className={`${isEmpty ? "flex-1 min-h-0 flex flex-col w-full" : ""}`}>
        <div className="flex flex-col gap-5 mb-4">
          <div className="flex items-center justify-between">
            <h1 className="text-[22px] font-normal text-[#FEFCEB]">Payments</h1>
            {showControls && permissions.canManagePayments && (
              <button
                onClick={handleCreatePaymentClick}
                className="bg-[#00E7FF] hover:opacity-50 text-[#021529] text-[15px] font-medium px-4 py-2 rounded-full flex items-center gap-1 transition-all active:scale-95"
              >
                Create payment
              </button>
            )}
          </div>
        </div>

        {showControls && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-app-surface rounded-2xl px-5 py-5">
              <p className="text-[#FEFCEB] text-[25px] font-semibold">{getCurrencySymbol(payments[0]?.currency || 'GBP')} {stats.totalPaid.toLocaleString()}</p>
              <p className="text-app-text-secondary text-[13px] mt-1">Total received</p>
            </div>
            <div className="bg-app-surface rounded-2xl px-5 py-5">
              <p className="text-[#FEFCEB] text-[25px] font-semibold">{getCurrencySymbol(payments[0]?.currency || 'GBP')} {stats.totalPending.toLocaleString()}</p>
              <p className="text-app-text-secondary text-[13px] mt-1">Total pending</p>
            </div>
            <div className="bg-app-surface rounded-2xl px-5 py-5">
              <p className="text-[#FEFCEB] text-[25px] font-semibold">{getCurrencySymbol(payments[0]?.currency || 'GBP')} {stats.totalOverdue.toLocaleString()}</p>
              <p className="text-app-text-secondary text-[13px] mt-1">Total overdue</p>
            </div>
          </div>
        )}

        {showControls && (
          <PaymentsSearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            dateFilter={dateFilter}
            onDateFilterChange={(value, from, to, label) => setDateFilter({ value, from: from || '', to: to || '', label })}
            paymentFilter={paymentFilter}
            onPaymentFilterChange={setPaymentFilter}
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ClubbieLoader />
          </div>
        ) : filteredPayments.length > 0 ? (
          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
              {filteredPayments.map(payment => (
                <div
                  key={payment.id}
                  onClick={() => {
                    // if (payment.payFor === 'Subscription' || payment.payFor === 'Custom') {
                    navigate(`/payments/payment-details/${payment.requestId}`);
                  }}
                  className="bg-app-surface border-[0.5px] border-app-border rounded-[20px] p-4 flex flex-col cursor-pointer hover:border-blue-shade-2 transition-colors relative"
                  style={{ height: '105px' }}
                >
                  <div className="flex justify-between mb-1">
                    <p className="text-app-text text-[15px]">{payment.description}</p>
                    <span className="text-app-text text-[15px]">{getCurrencySymbol(payment.currency)} {payment.amount.toLocaleString()}</span>
                  </div>

                  <div className="flex justify-between items-center mb-1">
                    <div>
                      <span className="text-app-text-secondary text-xs">
                        {payment.payFor === 'Subscription' ? `Next payment due on ${payment.date}` : `Payment due on ${payment.date}`}
                      </span>
                    </div>
                    <div className="flex text-right text-xs">
                      {/* Status Label Logic */}
                      {payment.status === 'overdue' && (
                        <span className="text-app-error">Overdue</span>
                      )}
                      {payment.status === 'unpaid' && (
                        <span className="text-[#FFC107]">Pending</span>
                      )}
                      {payment.status === 'paid' && (
                        <span className="text-app-accent-green">Paid</span>
                      )}
                      {/* {payment.status === 'pending-cash-approval' && (
                        <span className="text-[#FFC107]">{payment.cashApprovalCount} cash approval{payment.cashApprovalCount > 1 ? 's' : ''}</span>
                      )} */}
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-app-text-secondary text-xs">
                      {getCurrencySymbol(payment.currency)} {(payment.totalPaid || 0).toLocaleString()}/{(payment.totalAmount || payment.amount).toLocaleString()} received
                    </span>
                    {payment.cashApprovalCount > 0 && (
                      <span className="text-[#FFC107] text-xs">{payment.cashApprovalCount} cash approval{payment.cashApprovalCount > 1 ? 's' : ''}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (hasActiveFilters || hasEverLoaded.current) ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-[#7F8585] text-sm">No payments match your current filters.</p>
          </div>
        ) : showSyncStripe ? (
          <SyncStripeAccount />
        ) : (
          <EmptyState
            illustration={<PaymentsIllustration />}
            heading="No payments set up yet"
            description="Tap the button below to request your first payment."
            actions={
              permissions.canManagePayments ? (
                <button
                  className="w-[343px] h-11 bg-[#00E7FF] text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity mx-auto"
                  onClick={handleCreatePaymentClick}
                >
                  Create payment
                </button>
              ) : null
            }
          />
        )}
      </div>

      <CreatePaymentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => fetchPayments()}
      />
    </div>
  );
};

/**
 * Blob illustration with payment card placeholders (from Figma)
 */
const PaymentsIllustration = () => {
  const scale = 0.85;

  const PaymentCard = ({ bgColor, left, top, width, height, isVertical = false }) => (
    <div
      className="absolute flex overflow-hidden"
      style={{
        left: `${left * scale}px`,
        top: `${top * scale}px`,
        width: `${width * scale}px`,
        height: `${height * scale}px`,
        background: bgColor,
        borderRadius: `${11.887 * scale}px`,
        padding: `${15.85 * scale}px`,
        paddingTop: `${11.887 * scale}px`,
        paddingBottom: `${11.887 * scale}px`,
        flexDirection: isVertical ? "column" : "row",
        alignItems: isVertical ? "center" : "end",
        justifyContent: isVertical ? "end" : "center",
        gap: `${15.85 * scale}px`,
      }}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{ width: `${110.949 * scale}px`, gap: `${8.639 * scale}px` }}
      >
        <div
          className="relative rounded-full"
          style={{ width: `${48.797 * scale}px`, height: `${48.797 * scale}px`, background: "#E5E5D6" }}
        >
          <span
            className="absolute"
            style={{
              left: `${24.3 * scale}px`,
              top: `${12.2 * scale}px`,
              transform: "translateX(-50%)",
              fontSize: `${18.299 * scale}px`,
              fontWeight: 500,
              color: "#333E48",
              lineHeight: "1.3",
            }}
          >
            $
          </span>
        </div>
        <div className="flex flex-col items-center justify-center w-full" style={{ gap: `${8.639 * scale}px` }}>
          <div style={{ width: `${45.347 * scale}px`, height: `${8.503 * scale}px`, background: "#4C565D", borderRadius: "999px" }} />
          <div style={{ width: `${97.528 * scale}px`, height: `${8.503 * scale}px`, background: "#E5E5D6", borderRadius: "999px" }} />
        </div>
      </div>

      <div
        className="flex flex-col items-start justify-center"
        style={{ width: `${110.949 * scale}px`, gap: `${8.639 * scale}px` }}
      >
        <div className="rounded-full" style={{ width: `${12.199 * scale}px`, height: `${12.199 * scale}px`, background: "#E5E5D6" }} />
        {isVertical ? (
          <>
            <div style={{ width: `${45.347 * scale}px`, height: `${8.503 * scale}px`, background: "#4C565D", borderRadius: "999px" }} />
            <div className="flex" style={{ gap: `${3.05 * scale}px`, width: "100%" }}>
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
            </div>
            <div style={{ width: "100%", height: `${8.503 * scale}px`, background: "#E5E5D6", borderRadius: "999px" }} />
            <div style={{ width: "100%", height: `${8.503 * scale}px`, background: "#E5E5D6", borderRadius: "999px" }} />
          </>
        ) : (
          <>
            <div className="flex" style={{ gap: `${3.05 * scale}px`, width: "100%" }}>
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
              <div className="flex-1" style={{ height: `${8.503 * scale}px`, background: "#B2B5AD", borderRadius: "999px" }} />
            </div>
            <div style={{ width: `${45.347 * scale}px`, height: `${8.503 * scale}px`, background: "#4C565D", borderRadius: "999px" }} />
            <div style={{ width: `${97.528 * scale}px`, height: `${8.503 * scale}px`, background: "#E5E5D6", borderRadius: "999px" }} />
            <div style={{ width: `${97.528 * scale}px`, height: `${8.503 * scale}px`, background: "#E5E5D6", borderRadius: "999px" }} />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative" style={{ width: `${558.091 * scale}px`, height: `${405.628 * scale}px` }}>
      <svg
        className="absolute inset-0"
        width={558.091 * scale}
        height={405.628 * scale}
        viewBox="0 0 559 406"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M339.545 6.20268C411.725 19.193 441.826 75.935 488.464 117.739C526.878 152.172 554.142 181.017 557.358 225.153C560.691 270.895 554.227 315.56 504.986 344.484C459.632 371.126 397.148 361.309 339.545 368.638C257.901 379.027 174.76 425.839 103.234 395.602C26.1553 363.018 2.27533 289.576 0.130362 225.153C-2.06828 159.118 23.3438 90.3496 91.6046 46.3158C157.237 3.9774 254.113 -9.17278 339.545 6.20268Z"
          fill="#081725"
        />
      </svg>

      <div
        className="absolute"
        style={{
          left: `${54.9 * scale}px`,
          top: `${94.54 * scale}px`,
          width: `${405.628 * scale}px`,
          height: `${215.414 * scale}px`,
        }}
      >
        <PaymentCard bgColor="#989D99" left={0} top={0} width={268.913} height={104.095} />
        <PaymentCard bgColor="#666E71" left={0} top={111.3} width={268.913} height={104.095} />
        <PaymentCard bgColor="#26333D" left={277.53} top={0} width={128.093} height={215.013} isVertical />
      </div>
    </div>
  );
};

export default PaymentsList;
